import { initializeApp } from 'firebase/app';
import {
  getAuth, RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider,
  signInWithCredential, onAuthStateChanged, signOut,
} from 'firebase/auth';
import {
  initializeFirestore, collection, doc, getDoc, getDocFromServer, setDoc, updateDoc, onSnapshot,
  query, where, orderBy, serverTimestamp, addDoc, limit, deleteField, getDocs, startAfter, Timestamp,
} from 'firebase/firestore';
import { getDatabase, ref, set, update, remove, onValue, onDisconnect, serverTimestamp as rtdbServerTimestamp } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, CustomProvider } from 'firebase/app-check';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { getAnalytics, logEvent, isSupported as isAnalyticsSupported } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { shouldUseNativePhoneAuth } from '../utils/platform';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-CZ6Q4LKSR8',
};

const app = initializeApp(firebaseConfig);

// --- App Check --------------------------------------------------------------
// DISABLED BY DEFAULT. Server-side App Check enforcement is OFF (see the note in
// firebase/functions/index.js — enforceAppCheck is not set on any callable, and
// Firestore/RTDB/Storage are unenforced too). While that's the case, minting a
// Play Integrity / reCAPTCHA token on every cold start buys nothing and adds
// 1–4s to the startup critical path (worst right after a Play release, before
// Google recognises the new build). So we skip init entirely unless
// REACT_APP_APPCHECK_ENABLED is '1'/'true'.
//
// To re-enable (do this as its own change, tested across a release):
//   1. set REACT_APP_APPCHECK_ENABLED=1 in driver/.env.production, ship a build,
//      leave it in App Check *monitoring* mode for a few releases to confirm the
//      verified-traffic % is healthy;
//   2. only then flip enforceAppCheck on the server.
// Both switches are required — the client flag alone changes nothing.
//
//  - Native (Capacitor Android): @capacitor-firebase/app-check runs Play
//    Integrity attestation natively; its token is bridged into the Firebase JS
//    SDK via a CustomProvider so httpsCallable / Firestore / Storage requests
//    from the WebView carry X-Firebase-AppCheck exactly like the web path.
//  - Web: reCAPTCHA Enterprise via the Firebase JS SDK.
const APPCHECK_ENABLED = ['1', 'true'].includes(
  String(process.env.REACT_APP_APPCHECK_ENABLED || '').toLowerCase(),
);
const initAppCheck = () => {
  if (!APPCHECK_ENABLED) return;
  try {
    if (Capacitor.isNativePlatform()) {
      // Play Integrity can only attest an app delivered by Play. For a locally
      // installed debug build set REACT_APP_APPCHECK_DEBUG=1 — the plugin logs a
      // debug token to logcat; register it under Firebase Console -> App Check
      // -> Apps -> Manage debug tokens.
      const nativeProvider = process.env.REACT_APP_APPCHECK_DEBUG === '1' ? 'debug' : 'playIntegrity';
      // Load the plugin + initialize lazily & memoized, so the provider below
      // registers synchronously (before getFirestore/getDatabase/getStorage) —
      // otherwise the first requests race the provider and go out unverified.
      let nativePluginPromise = null;
      const getNativePlugin = () => {
        if (!nativePluginPromise) {
          nativePluginPromise = import('@capacitor-firebase/app-check')
            .then(async ({ FirebaseAppCheck }) => {
              await FirebaseAppCheck.initialize({
                provider: nativeProvider,
                isTokenAutoRefreshEnabled: true,
              });
              return FirebaseAppCheck;
            })
            .catch((e) => { nativePluginPromise = null; throw e; });
        }
        return nativePluginPromise;
      };
      initializeAppCheck(app, {
        provider: new CustomProvider({
          // Bounded: Play Integrity can stall for seconds (especially right after
          // a Play release, before Google recognises the new build). Never let a
          // token fetch hang an Auth / Firestore / RTDB request — after 4s we
          // reject and the SDK proceeds with an unverified request instead of
          // blocking (a hung promise is unrecoverable; a rejection is not).
          getToken: async () => {
            const res = await Promise.race([
              (async () => { const p = await getNativePlugin(); return p.getToken(); })(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('appcheck_timeout')), 4000)),
            ]);
            return { token: res.token, expireTimeMillis: res.expireTimeMillis };
          },
        }),
        isTokenAutoRefreshEnabled: true,
      });
      return;
    }

    // Web
    const appCheckKey = process.env.REACT_APP_FIREBASE_APPCHECK_KEY;
    const appCheckDebugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
    if (appCheckDebugToken && typeof self !== 'undefined') {
      // Debug/dev builds: register this value under Firebase Console →
      // App Check → Apps → Manage debug tokens.
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken === 'true' ? true : appCheckDebugToken;
    }
    if (appCheckKey) {
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(appCheckKey),
        isTokenAutoRefreshEnabled: true,
      });
    } else {
      console.warn('App Check: REACT_APP_FIREBASE_APPCHECK_KEY not set — callable/Firestore requests will be rejected while enforcement is on.');
    }
  } catch (e) {
    console.error('App Check init failed:', e?.message || e);
  }
};
initAppCheck();

let analytics = null;
isAnalyticsSupported().then((supported) => {
  if (supported) {
    try { analytics = getAnalytics(app); } catch { /* analytics blocked/disabled */ }
  }
}).catch(() => {});

// Auto-instruments page load + network request timing — no manual traces
// needed for baseline data. WebView network calls (Firestore/RTDB/Functions/
// Maps) are made from JS, so this is what actually sees them — the native
// Android Performance plugin only sees native OkHttp traffic, which this app
// has almost none of.
try { getPerformance(app); } catch { /* perf blocked/disabled */ }

export const logAnalyticsEvent = (eventName, params = {}) => {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('mt_analytics_opt_out') === '1') {
      return;
    }
  } catch { /* */ }
  if (analytics) {
    try { logEvent(analytics, eventName, params); } catch { /* */ }
  }
};
export const auth = getAuth(app);
// Android WebViews can keep a Firestore WebChannel connection open while
// silently delaying writes/listener events on mobile networks. Long-polling is
// slightly less efficient but much more predictable for ride status changes.
export const db = initializeFirestore(app, { experimentalForceLongPolling: true });
export const rtdb = getDatabase(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'asia-south1');
export const getFirebaseMessaging = () => getMessaging(app);
export { getToken, isSupported, onMessage };

let firebaseAuthNativePlugin = null;
const getNativeAuthPlugin = () => {
  if (!firebaseAuthNativePlugin) {
    firebaseAuthNativePlugin = registerPlugin('FirebaseAuthentication');
  }
  return firebaseAuthNativePlugin;
};

const getPhoneAuthPlugin = () => (
  shouldUseNativePhoneAuth() ? getNativeAuthPlugin() : FirebaseAuthentication
);

const nativePhoneAuthCall = async (method, options) => {
  if (shouldUseNativePhoneAuth() && typeof Capacitor.nativePromise === 'function') {
    return Capacitor.nativePromise('FirebaseAuthentication', method, options);
  }
  return getPhoneAuthPlugin()[method](options);
};

let nativeVerificationId = null;
let nativePhoneError = null;
let nativePhoneListenersReady = false;
let nativeOtpAttempt = 0;
let nativePhonePendingPhone = null;
let nativeOtpCallbacks = { onAutoVerified: null, onCodeReceived: null };

const AUTH_VID_KEY = 'mt_driver_auth_vid';
const AUTH_PHONE_KEY = 'mt_driver_auth_phone';

export const setNativeOtpCallbacks = (callbacks = {}) => {
  nativeOtpCallbacks = { ...nativeOtpCallbacks, ...callbacks };
};

export const getPendingVerificationId = () => nativeVerificationId || getStoredVerificationId();

export const hasPendingOtpSession = () => Boolean(getPendingVerificationId());

export const getPendingOtpPhone = () => {
  try {
    return sessionStorage.getItem(AUTH_PHONE_KEY) || localStorage.getItem(AUTH_PHONE_KEY) || null;
  } catch {
    return null;
  }
};

const storeVerificationSession = (verificationId, phoneNumber) => {
  if (!verificationId) return;
  try {
    sessionStorage.setItem(AUTH_VID_KEY, verificationId);
    localStorage.setItem(AUTH_VID_KEY, verificationId);
    if (phoneNumber) {
      sessionStorage.setItem(AUTH_PHONE_KEY, phoneNumber);
      localStorage.setItem(AUTH_PHONE_KEY, phoneNumber);
    }
  } catch { /* */ }
};

const getStoredVerificationId = () => {
  try {
    return sessionStorage.getItem(AUTH_VID_KEY) || localStorage.getItem(AUTH_VID_KEY) || null;
  } catch {
    return null;
  }
};

const clearVerificationSession = () => {
  nativeVerificationId = null;
  try {
    sessionStorage.removeItem(AUTH_VID_KEY);
    sessionStorage.removeItem(AUTH_PHONE_KEY);
    localStorage.removeItem(AUTH_VID_KEY);
    localStorage.removeItem(AUTH_PHONE_KEY);
  } catch { /* */ }
};

const rememberVerificationId = (verificationId, phoneNumber) => {
  nativeVerificationId = verificationId;
  storeVerificationSession(verificationId, phoneNumber);
};

const waitForVerificationId = (maxMs = 8000) => new Promise((resolve) => {
  const existing = nativeVerificationId || getStoredVerificationId();
  if (existing) {
    resolve(existing);
    return;
  }
  const started = Date.now();
  const poll = setInterval(() => {
    const id = nativeVerificationId || getStoredVerificationId();
    if (id) {
      clearInterval(poll);
      resolve(id);
    } else if (Date.now() - started > maxMs) {
      clearInterval(poll);
      resolve(null);
    }
  }, 200);
});

const waitForAuthUser = (maxMs = 4000) => new Promise((resolve) => {
  if (auth.currentUser) {
    resolve(auth.currentUser);
    return;
  }
  const timeout = setTimeout(() => {
    unsub();
    resolve(null);
  }, maxMs);
  const unsub = onAuthStateChanged(auth, (user) => {
    if (user) {
      clearTimeout(timeout);
      unsub();
      resolve(user);
    }
  });
});

const signInWithNativeCredential = async (verificationId, code) => {
  const credential = PhoneAuthProvider.credential(verificationId, code);
  const result = await signInWithCredential(auth, credential);
  clearVerificationSession();
  return result.user;
};

// A React development render and the background retry may ask for the same
// bridge together. Keep one native sign-in operation per uid so they cannot
// race and leave the native layer signed in as an unexpected account.
let nativeAuthBridge = null;

const nativeAuthBridgeError = (code, cause) => Object.assign(
  new Error(code),
  { code, cause },
);

const withinNativeAuthBridgeBudget = (promise) => new Promise((resolve, reject) => {
  const timer = setTimeout(
    () => reject(nativeAuthBridgeError('native-auth-bridge-timeout')),
    12000,
  );
  Promise.resolve(promise).then(
    (value) => { clearTimeout(timer); resolve(value); },
    (error) => { clearTimeout(timer); reject(error); },
  );
});

/**
 * Give the native foreground service a Firebase Auth session matching the
 * already-authenticated WebView user. The service writes directly through the
 * native Firebase SDK after a WebView is killed, so merely starting it is not
 * enough: this function resolves only after native auth confirms the same uid.
 *
 * A caller that is about to mark an Anna online must await this. Startup may
 * also call it optimistically; failures are deliberately surfaced to that
 * caller so it can retry, rather than being silently mistaken for success.
 */
export const bridgeNativeAuthSession = async (uid) => {
  if (!uid || !Capacitor.isNativePlatform()) return { native: false, uid: null };
  if (nativeAuthBridge?.uid === uid) return nativeAuthBridge.promise;

  const promise = (async () => {
    if (auth.currentUser?.uid !== uid) {
      throw nativeAuthBridgeError('native-auth-bridge-stale-web-session');
    }

    const current = await withinNativeAuthBridgeBudget(FirebaseAuthentication.getCurrentUser());
    if (current?.user?.uid === uid) return { native: true, uid };

    const fn = httpsCallable(functions, 'mintNativeAuthToken');
    const res = await withinNativeAuthBridgeBudget(fn({}));
    const token = res.data?.customToken;
    if (!token) throw nativeAuthBridgeError('native-auth-bridge-missing-token');

    // Do not let a completed call from a just-signed-out account establish a
    // native session after another user has taken over the WebView.
    if (auth.currentUser?.uid !== uid) {
      throw nativeAuthBridgeError('native-auth-bridge-stale-web-session');
    }

    await withinNativeAuthBridgeBudget(
      FirebaseAuthentication.signInWithCustomToken({ token, skipNativeAuth: false }),
    );
    const confirmed = await withinNativeAuthBridgeBudget(FirebaseAuthentication.getCurrentUser());
    if (confirmed?.user?.uid !== uid) {
      throw nativeAuthBridgeError('native-auth-bridge-uid-mismatch');
    }
    return { native: true, uid };
  })();

  nativeAuthBridge = { uid, promise };
  try {
    return await promise;
  } finally {
    if (nativeAuthBridge?.promise === promise) nativeAuthBridge = null;
  }
};

/**
 * The WebView and the native Firebase SDK intentionally use separate auth
 * sessions. Explicitly clear the native one only after tracking has been
 * stopped, so a sticky Android service cannot keep a previous Anna's session
 * alive after Offline or Logout.
 */
export const endNativeAuthSession = async () => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await FirebaseAuthentication.signOut();
  } catch (e) {
    // Cleanup is idempotent: a process already torn down has nothing left to
    // sign out, and its RTDB offline command still prevents later writes.
    console.warn('native Firebase sign-out failed:', e?.message || e);
  }
};

const waitForNativeVerificationId = (attemptId) => new Promise((resolve, reject) => {
  const started = Date.now();
  const poll = setInterval(() => {
    if (attemptId !== nativeOtpAttempt) {
      clearInterval(poll);
      reject(Object.assign(new Error('OTP request superseded'), { code: 'auth/superseded' }));
      return;
    }
    if (nativeVerificationId) {
      clearInterval(poll);
      resolve({ verificationId: nativeVerificationId });
      return;
    }
    if (auth.currentUser) {
      clearInterval(poll);
      resolve({ autoVerified: true });
      return;
    }
    if (nativePhoneError) {
      clearInterval(poll);
      const err = nativePhoneError;
      nativePhoneError = null;
      reject(err);
    } else if (Date.now() - started > 90000) {
      clearInterval(poll);
      reject(Object.assign(new Error('OTP request timed out'), { code: 'auth/timeout' }));
    }
  }, 200);
});

const ensureNativePhoneListeners = async () => {
  if (nativePhoneListenersReady || !shouldUseNativePhoneAuth()) return;
  const plugin = getPhoneAuthPlugin();
  await Promise.all([
    plugin.addListener('phoneCodeSent', (event) => {
      const vid = event?.verificationId;
      if (vid) rememberVerificationId(vid, nativePhonePendingPhone || getPendingOtpPhone());
    }),
    plugin.addListener('phoneVerificationCompleted', async (event) => {
      // skipNativeAuth: true — do not let native consume the SMS; sign in via WebView Auth.
      const code = event?.verificationCode;
      if (code) {
        const vid = nativeVerificationId || getStoredVerificationId() || await waitForVerificationId(8000);
        if (!vid) {
          nativeOtpCallbacks.onCodeReceived?.(code);
          return;
        }
        try {
          if (auth.currentUser) {
            clearVerificationSession();
            nativeOtpCallbacks.onAutoVerified?.();
            return;
          }
          await signInWithNativeCredential(vid, code);
          nativeOtpCallbacks.onAutoVerified?.();
        } catch {
          if (auth.currentUser) {
            clearVerificationSession();
            nativeOtpCallbacks.onAutoVerified?.();
            return;
          }
          nativeOtpCallbacks.onCodeReceived?.(code);
        }
        return;
      }
      const user = await waitForAuthUser(8000);
      if (user) {
        clearVerificationSession();
        nativeOtpCallbacks.onAutoVerified?.();
      }
    }),
    plugin.addListener('phoneVerificationFailed', (event) => {
      const raw = event?.message || event?.code || 'Native OTP failed';
      nativePhoneError = Object.assign(new Error(raw), {
        code: event?.code || 'auth/native-otp-failed',
      });
    }),
  ]);
  nativePhoneListenersReady = true;
};

const AUTH_ERROR_KEYS = {
  'auth/invalid-phone-number': 'authErrors.invalidPhone',
  'auth/too-many-requests': 'authErrors.tooManyRequests',
  'auth/captcha-check-failed': 'authErrors.captchaFailed',
  'auth/argument-error': 'authErrors.captchaFailed',
  'auth/invalid-verification-code': 'authErrors.invalidOtp',
  'auth/code-expired': 'authErrors.codeExpired',
  'auth/missing-verification-code': 'authErrors.missingOtp',
  'auth/quota-exceeded': 'authErrors.quotaExceeded',
  'auth/app-not-authorized': 'authErrors.appNotAuthorized',
  'auth/operation-not-allowed': 'authErrors.phoneNotEnabled',
  'auth/network-request-failed': 'authErrors.networkFailed',
  'auth/native-otp-failed': 'authErrors.default',
  'auth/timeout': 'authErrors.default',
  'auth/missing-otp-session': 'authErrors.noOtpSession',
};

export const getAuthErrorKey = (error) => {
  const code = error?.code || '';
  const msg = error?.message || '';
  if (msg.includes('region enabled')) return 'authErrors.regionNotEnabled';
  if (AUTH_ERROR_KEYS[code]) return AUTH_ERROR_KEYS[code];
  if (code.includes('requests-from-referer') || msg.includes('referer')) {
    return 'authErrors.refererBlocked';
  }
  if (code === 'auth/network-request-failed' || msg.includes('Failed to fetch') || msg.includes('ERR_FAILED')) {
    return 'authErrors.networkFailed';
  }
  if (msg.includes('No OTP session')) return 'authErrors.noOtpSession';
  const combined = `${code} ${msg}`.toLowerCase();
  if (combined.includes('too-many') || combined.includes('too many')) return 'authErrors.tooManyRequests';
  if (combined.includes('quota')) return 'authErrors.quotaExceeded';
  return 'authErrors.default';
};

const ensureRecaptchaContainer = () => {
  let el = document.getElementById('recaptcha-container');
  if (!el) {
    el = document.createElement('div');
    el.id = 'recaptcha-container';
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = 'position:fixed;left:-9999px;bottom:0;width:1px;height:1px;overflow:hidden';
    document.body.appendChild(el);
  }
  return el;
};

const getRecaptchaVerifier = async () => {
  ensureRecaptchaContainer();
  if (window.recaptchaVerifier) {
    try { await window.recaptchaVerifier.clear(); } catch { /* */ }
    window.recaptchaVerifier = null;
  }
  window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
    size: 'invisible',
    callback: () => {},
    'expired-callback': () => {
      window.recaptchaVerifier = null;
      window.confirmationResult = null;
    },
  });
  await window.recaptchaVerifier.render();
  return window.recaptchaVerifier;
};

/**
 * Server-side cost guard, called before Firebase Auth is ever asked to send
 * a real SMS (~Rs.6.69/message). Firebase's own phone-auth send doesn't run
 * through our backend, so this is a separate check up front rather than
 * something wrapped around it. A rejection throws with "too many" in the
 * message, which getAuthErrorKey already maps to authErrors.tooManyRequests
 * — same toast a real Firebase rate-limit shows, no new UI path needed.
 */
const checkOtpCooldown = async (phoneNumber) => {
  const phoneDigits = String(phoneNumber || '').replace(/\D/g, '').slice(-10);
  await httpsCallable(functions, 'checkOtpCooldown')({ phone: phoneDigits });
};

export const sendOtp = async (phoneNumber) => {
  await checkOtpCooldown(phoneNumber);
  window.confirmationResult = null;
  if (shouldUseNativePhoneAuth()) {
    try {
      await ensureNativePhoneListeners();
      clearVerificationSession();
      nativeOtpAttempt += 1;
      const attemptId = nativeOtpAttempt;
      nativePhoneError = null;
      nativePhonePendingPhone = phoneNumber;
      // skipNativeAuth: keep OTP unused until WebView signInWithCredential (avoids code-expired).
      await nativePhoneAuthCall('signInWithPhoneNumber', {
        phoneNumber,
        timeout: 120,
        skipNativeAuth: true,
      });
      const waitResult = await waitForNativeVerificationId(attemptId);
      if (waitResult.autoVerified) {
        const user = await waitForAuthUser(8000);
        if (user) {
          clearVerificationSession();
          return { autoVerified: true };
        }
      }
      const verificationId = nativeVerificationId || waitResult?.verificationId || '';
      if (verificationId) {
        storeVerificationSession(verificationId, phoneNumber);
        return { native: true, verificationId };
      }
      throw Object.assign(new Error('Native OTP session missing'), { code: 'auth/missing-otp-session' });
    } catch (e) {
      console.warn('Native OTP failed:', e?.message || e);
      throw e;
    } finally {
      nativePhonePendingPhone = null;
    }
  }

  try {
    const verifier = await getRecaptchaVerifier();
    window.confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier);
    return { web: true };
  } catch (e) {
    console.error('sendOtp failed:', e?.code, e?.message, e);
    throw e;
  }
};

const isOtpVerifyFatal = (error) => {
  const code = String(error?.code || '').toLowerCase();
  const msg = String(error?.message || '').toLowerCase();
  const combined = `${code} ${msg}`;
  return combined.includes('code-expired')
    || combined.includes('session-expired')
    || combined.includes('invalid-verification-code')
    || combined.includes('invalid verification code');
};

export const verifyOtp = async (otp) => {
  if (auth.currentUser) return auth.currentUser;
  if (shouldUseNativePhoneAuth()) {
    let verificationId = nativeVerificationId || getStoredVerificationId();
    if (!verificationId) verificationId = await waitForVerificationId(15000);
    if (!verificationId) {
      throw Object.assign(new Error('No OTP session'), { code: 'auth/missing-otp-session' });
    }
    try {
      return await signInWithNativeCredential(verificationId, otp);
    } catch (credErr) {
      if (isOtpVerifyFatal(credErr)) clearVerificationSession();
      throw credErr;
    }
  }
  if (!window.confirmationResult) {
    if (auth.currentUser) return auth.currentUser;
    throw Object.assign(new Error('No OTP session'), { code: 'auth/missing-otp-session' });
  }
  try {
    const result = await window.confirmationResult.confirm(otp);
    return result.user;
  } catch (err) {
    if (auth.currentUser) return auth.currentUser;
    throw err;
  }
};

export const logout = async (uid) => {
  clearVerificationSession();
  if (uid) {
    // Keep each cleanup independent. A transient Firestore/RTDB failure must
    // not skip stopping tracking or removing the device's push token.
    try { await setDriverOnline(uid, false); } catch { /* best-effort offline */ }
    try { await clearDriverLocation(uid); } catch { /* best-effort location cleanup */ }
    try {
      // Drop this device's push token so ride requests stop landing here and a
      // different driver logging in on the same device doesn't inherit it.
      const { clearDriverMessaging } = await import('./notifications');
      await clearDriverMessaging(uid);
    } catch { /* best-effort push cleanup */ }
  }

  // Stop the foreground service directly, rather than waiting for a Firestore
  // listener that may no longer exist while the app is closing. The native
  // session is cleared only afterwards, preserving the service's ability to
  // process the explicit RTDB offline command during shutdown.
  try {
    const { stopBackgroundLocation } = await import('../utils/backgroundLocation');
    await stopBackgroundLocation();
  } catch { /* native plugin may already be gone during teardown */ }
  await endNativeAuthSession();
  return signOut(auth);
};

export const getDriver = async (uid) => {
  const snap = await getDoc(doc(db, 'drivers', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/**
 * Authoritative profile read — forces a server round-trip so it can't be fooled
 * by an empty cold cache or a pending local merge-write shadowing the real doc
 * (which used to misroute a returning driver into registration right after OTP).
 * Throws if the client is offline — callers should fall back to getDriver / cache.
 */
export const getDriverFromServer = async (uid) => {
  const snap = await getDocFromServer(doc(db, 'drivers', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const ensureDriverDefaults = async (uid, phone, { authoritative = false } = {}) => {
  if (!uid) return null;
  // Startup routing must never classify a returning driver from a transient
  // local-cache snapshot. Callers that decide between Dashboard and
  // registration use an explicit server read; background repair callers can
  // keep the faster cache-tolerant behaviour.
  const existing = authoritative
    ? await getDriverFromServer(uid)
    : await getDriver(uid);
  const clean10 = String(phone || '').replace(/\D/g, '').slice(-10);
  const normPhone = clean10.length === 10 ? `+91${clean10}` : (phone || '');

  // If existing profile already has name, vehicleNumber, or is approved, preserve it
  if (existing && (existing.name || existing.vehicleNumber || existing.isApproved || existing.profileComplete)) {
    if (normPhone && normPhone !== existing.phone) {
      await setDoc(doc(db, 'drivers', uid), { phone: normPhone, updatedAt: serverTimestamp() }, { merge: true });
      return { ...existing, phone: normPhone };
    }
    return existing;
  }

  // Auth uid changed (phone re-verification) but this captain already has an
  // approved profile under an old uid? Re-link it server-side — the client can't
  // copy an approved profile itself (drivers create/update rules block
  // isApproved / pendingDues), which used to force them to re-register.
  if (clean10.length === 10) {
    try {
      const fn = httpsCallable(functions, 'linkDriverProfile');
      const res = await fn({});
      if (res.data?.linked) {
        const relinked = authoritative
          ? await getDriverFromServer(uid)
          : await getDriver(uid);
        if (relinked) return relinked;
      }
    } catch (e) {
      // A failed authoritative relink is an unresolved state, not proof that
      // this is a new driver. Propagate it so the router keeps the safe cached
      // dashboard/skeleton instead of incorrectly opening registration.
      if (authoritative) throw e;
      console.warn('linkDriverProfile failed (non-fatal):', e?.message || e);
    }
  }

  if (existing) {
    if (normPhone && normPhone !== existing.phone) {
      await setDoc(doc(db, 'drivers', uid), { phone: normPhone, updatedAt: serverTimestamp() }, { merge: true });
      return { ...existing, phone: normPhone };
    }
    return existing;
  }

  const patch = {
    phone: normPhone,
    profileComplete: false,
    isApproved: false,
    isOnline: false,
    isBlocked: false,
    vehicleType: 'auto',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(doc(db, 'drivers', uid), patch, { merge: true });
  return { id: uid, ...patch, phone: normPhone };
};

/** Drop any string value that is an inline data: URL — those must never be persisted
 *  to Firestore (1 MB doc limit + bypasses Storage rules). Recurses into nested maps. */
const stripDataUrls = (value) => {
  if (typeof value === 'string') return value.startsWith('data:') ? undefined : value;
  if (Array.isArray(value)) return value.map(stripDataUrls);
  if (value && typeof value === 'object') {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      const cleaned = stripDataUrls(v);
      if (cleaned !== undefined) out[k] = cleaned;
    });
    return out;
  }
  return value;
};

export const updateDriverProfile = async (uid, data) => {
  await setDoc(doc(db, 'drivers', uid), { ...stripDataUrls(data), updatedAt: serverTimestamp() }, { merge: true });
};

export const listenToDriverProfile = (uid, callback) =>
  onSnapshot(doc(db, 'drivers', uid), (snap) => {
    callback(
      snap.exists() ? { id: snap.id, ...snap.data() } : null,
      {
        fromCache: snap.metadata.fromCache,
        hasPendingWrites: snap.metadata.hasPendingWrites,
        authoritative: !snap.metadata.fromCache && !snap.metadata.hasPendingWrites,
      },
    );
  });

// This document is read-only to signed-in clients and writable only by an
// MFA-verified admin (see firestore.rules). Keep release messaging on the same
// controlled path as the existing maintenance mode.
export const listenToPlatformConfig = (callback) => onSnapshot(
  doc(db, 'config', 'platform'),
  (snap) => callback(snap.exists() ? snap.data() : null),
  () => callback(null),
);

// The RTDB rule for driverLocations/{uid} rejects every write while this
// command says 'offline' (see database.rules.json) — a deliberate backstop so
// a force-killed app can't keep streaming GPS after going offline. Exported
// on its own so a caller can clear it *before* attempting a location publish,
// not just inside setDriverOnline (which normally runs after — too late to
// unblock that same publish).
export const clearRemoteOfflineCommand = async (uid) => {
  if (!uid) return;
  await remove(ref(rtdb, `driverPresenceControl/${uid}`));
};

export const setDriverOnline = async (uid, isOnline) => {
  if (!uid) return;
  const controlRef = ref(rtdb, `driverPresenceControl/${uid}`);

  // Clear the remote stop command before publishing isOnline=true. This keeps
  // the native service from seeing a stale Admin-offline command during the
  // restart transition. A failed clear must block the transition; otherwise
  // the UI could say Online while RTDB correctly rejects every location write.
  if (isOnline) {
    await clearRemoteOfflineCommand(uid);
  }
  await updateDoc(doc(db, 'drivers', uid), {
    isOnline,
    updatedAt: serverTimestamp(),
  });

  if (!isOnline) {
    // The Firestore listener stops the foreground service when the app is open;
    // this command covers the harder case where only the native service is
    // alive after the WebView was backgrounded or killed.
    await set(controlRef, {
      state: 'offline',
      source: 'driver',
      requestedAt: Date.now(),
    }).catch((error) => {
      // Firestore is still authoritative and the local effects stop tracking;
      // leave a diagnostic rather than turning a successful offline tap into a
      // confusing error after the driver's state already changed.
      console.warn('driver remote offline command failed:', error?.message || error);
    });
  }
};

export const saveFcmToken = async (uid, token) => {
  if (!token) return;
  await setDoc(doc(db, 'drivers', uid), { fcmToken: token, updatedAt: serverTimestamp() }, { merge: true });
};

/**
 * Reports the installed native app's own version (not the bundled JS's) so
 * dispatch.js can tell which drivers' apps carry the native ride-offer alert
 * code before ever sending them a data-only push — see
 * utils/requiredUpdate.js#getInstalledAppVersion for why the native version
 * is the trustworthy source here. Best-effort: a failed report must never
 * block push registration, and simply leaves that driver on the safe,
 * always-worked notification path server-side.
 */
export const saveInstalledAppVersion = async (uid, version) => {
  if (!uid || !version) return;
  try {
    await setDoc(doc(db, 'drivers', uid), { appVersion: version, updatedAt: serverTimestamp() }, { merge: true });
  } catch (e) {
    console.warn('saveInstalledAppVersion failed:', e?.message || e);
  }
};

/** Remove this device's push token from the driver doc (on logout / account switch). */
export const clearFcmToken = async (uid) => {
  if (!uid) return;
  try {
    await updateDoc(doc(db, 'drivers', uid), { fcmToken: deleteField(), updatedAt: serverTimestamp() });
  } catch (e) {
    console.warn('clearFcmToken failed:', e?.message);
  }
};

export const registerDriverPush = async (uid) => {
  const { initDriverMessaging } = await import('./notifications');
  return initDriverMessaging(uid);
};

/**
 * Subscribe only to rides the dispatch service has explicitly offered to this
 * Anna. This is intentionally not a client-side distance filter: the server
 * owns ring eligibility and this query prevents unrelated Annas from seeing
 * passenger pickup/drop details or receiving an alert.
 */
export const listenToSearchingRides = (driverId, callback, onError) => {
  if (!driverId) return () => {};
  const q = query(
    collection(db, 'rides'),
    where('status', '==', 'searching'),
    where('notifiedDriverIds', 'array-contains', driverId),
    limit(20),
  );
  return onSnapshot(
    q,
    (snap) => {
      const rides = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rides.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      callback(rides);
    },
    (err) => {
      console.error('listenToSearchingRides:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

export const listenToActiveDriverRide = (driverId, callback, onError) => {
  const q = query(
    collection(db, 'rides'),
    where('driverId', '==', driverId),
    where('status', 'in', ['driver_assigned', 'arrived', 'started']),
    limit(1),
  );
  return onSnapshot(
    q,
    (snap) => {
      const active = snap.docs.length ? { id: snap.docs[0].id, ...snap.docs[0].data() } : null;
      callback(active);
    },
    (err) => {
      console.error('listenToActiveDriverRide error:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

export const listenToDriverRides = (driverId, callback, onError) => {
  const q = query(
    collection(db, 'rides'),
    where('driverId', '==', driverId),
    orderBy('createdAt', 'desc'),
    limit(40),
  );
  let unsub = onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.warn('listenToDriverRides ordered query failed, falling back:', err?.code, err?.message);
      try { unsub?.(); } catch { /* */ }
      // Keep the createdAt ordering in the fallback too — without it Firestore
      // returns the 80 lexicographically-smallest doc IDs (oldest rides), so a
      // veteran captain's recent history silently disappears (L5). If the
      // composite index really is missing this surfaces via err2 instead of
      // showing wrong data.
      const fallback = query(
        collection(db, 'rides'),
        where('driverId', '==', driverId),
        orderBy('createdAt', 'desc'),
        limit(80),
      );
      unsub = onSnapshot(
        fallback,
        (snap) => {
          callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        },
        (err2) => {
          console.error('listenToDriverRides fallback error:', err2?.code, err2?.message);
          onError?.(err2);
        },
      );
    },
  );
  return () => {
    try { unsub?.(); } catch { /* */ }
  };
};

export const listenToRide = (rideId, callback, onError) =>
  onSnapshot(
    doc(db, 'rides', rideId),
    (snap) => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => {
      console.error('listenToRide:', err?.code, err?.message);
      onError?.(err);
    },
  );

/**
 * Completed rides since `since` (a Date) for accurate today/week earnings —
 * independent of the 40-row recent-rides window, which under-counts a busy
 * captain's week. Scoped to a week, so bounded.
 */
export const listenToCompletedRidesSince = (driverId, since, callback, onError) => {
  if (!driverId || !since) return () => {};
  const q = query(
    collection(db, 'rides'),
    where('driverId', '==', driverId),
    where('status', '==', 'completed'),
    where('completedAt', '>=', Timestamp.fromDate(since)),
    orderBy('completedAt', 'desc'),
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('listenToCompletedRidesSince:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

/** One-shot page of older rides for the history tab (no realtime listener). */
export const fetchOlderDriverRides = async (driverId, beforeCreatedAt, pageSize = 30) => {
  if (!driverId) return [];
  try {
    const parts = [
      collection(db, 'rides'),
      where('driverId', '==', driverId),
      orderBy('createdAt', 'desc'),
    ];
    if (beforeCreatedAt) parts.push(startAfter(beforeCreatedAt));
    parts.push(limit(pageSize));
    const snap = await getDocs(query(...parts));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('fetchOlderDriverRides failed:', e?.code, e?.message);
    return [];
  }
};

export const getRideFresh = async (rideId) => {
  try {
    const snap = await getDocFromServer(doc(db, 'rides', rideId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch {
    const snap = await getDoc(doc(db, 'rides', rideId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  }
};

export const acceptRide = async (rideId) => {
  try {
    const fn = httpsCallable(functions, 'acceptRide');
    await fn({ rideId });
    logAnalyticsEvent('ride_accepted', { rideId });
  } catch (e) {
    const code = e?.code || '';
    const msg = String(e?.message || '');
    if (
      code.includes('failed-precondition')
      || /already taken|already assigned|not available/i.test(msg)
    ) {
      const err = new Error('taken');
      err.code = 'taken';
      throw err;
    }
    throw e;
  }
};

// Intertown is a separate, explicitly assigned workflow. It intentionally does
// not join the local searching-rides listener or change the normal trip route.
export const listenToMyIntertownRequests = (driverId, callback) => {
  if (!driverId) return () => {};
  return onSnapshot(query(
    collection(db, 'intertownRequests'), where('assignedDriverId', '==', driverId), orderBy('createdAt', 'desc'), limit(10),
  ), (snap) => {
    const trips = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
    trips.sort((a, b) => (b?.createdAt?.toMillis?.() || 0) - (a?.createdAt?.toMillis?.() || 0));
    callback(trips);
  }, (error) => { console.warn('Intertown listener failed:', error?.message); callback([]); });
};

export const listenToOpenIntertownOffers = (callback) => onSnapshot(query(
  collection(db, 'intertownOpenOffers'), orderBy('openedAt', 'asc'), limit(5),
), (snap) => callback(snap.docs.map((item) => ({ id: item.id, ...item.data() }))), (error) => {
  // Rules intentionally hide this collection from Annas outside the approved
  // Intertown whitelist; an empty feed is the correct UI in that case.
  console.warn('Open Intertown offers listener:', error?.message);
  callback([]);
});

export const claimIntertownRequest = async (requestId) => (
  await httpsCallable(functions, 'claimIntertownRequest')({ requestId })
).data;

export const declineIntertownOffer = async (requestId) => (
  await httpsCallable(functions, 'declineIntertownOffer')({ requestId })
).data;

export const respondToIntertownOffer = async (requestId, accept) => (
  await httpsCallable(functions, 'respondToIntertownOffer')({ requestId, accept: Boolean(accept) })
).data;

export const updateIntertownTrip = async (requestId, action, location = null) => (
  await httpsCallable(functions, 'updateIntertownTrip')({ requestId, action, ...(location ? { location } : {}) })
).data;

export const updateRideStatus = async (rideId, status, extra = {}) => {
  if (status === 'started') {
    throw new Error('use_startRide');
  }
  const fn = httpsCallable(functions, 'driverUpdateRide');
  await fn({
    rideId,
    status,
    cancelReason: extra.cancelReason,
    trail: extra.trail || [],
  });
  if (status === 'completed') {
    logAnalyticsEvent('ride_completed', { rideId });
  }
};

export const startRideWithOtp = async (rideId, otp) => {
  const fn = httpsCallable(functions, 'startRide');
  await fn({ rideId, otp: String(otp || '').replace(/\D/g, '').slice(0, 4) });
};

export const startIntertownTripWithOtp = async (requestId, otp) => {
  const fn = httpsCallable(functions, 'startIntertownTrip');
  await fn({ requestId, otp: String(otp || '').replace(/\D/g, '').slice(0, 4) });
};

/** Fire-and-forget pre-warm pings so cold Cloud Run instances boot before user taps. */
export const prewarmDriverUpdateRide = async () => {
  try {
    const fn = httpsCallable(functions, 'driverUpdateRide');
    await fn({ warm: true });
  } catch { /* non-fatal pre-warm */ }
};

export const prewarmStartRide = async () => {
  try {
    const fn = httpsCallable(functions, 'startRide');
    await fn({ warm: true });
  } catch { /* non-fatal pre-warm */ }
};

export const cancelRideAsDriver = async (rideId, cancelReason = 'other') => {
  const fn = httpsCallable(functions, 'driverUpdateRide');
  await fn({ rideId, status: 'cancelled', cancelReason: String(cancelReason).slice(0, 80) });
};

export const triggerRideSos = async (rideId) => {
  const fn = httpsCallable(functions, 'triggerRideSos');
  const result = await fn({ rideId });
  return result.data;
};

export const reportRideAnomaly = async (payload) => {
  try {
    const fn = httpsCallable(functions, 'reportRideAnomaly');
    await fn(payload);
  } catch (err) {
    console.warn('reportRideAnomaly non-fatal error:', err?.message);
  }
};

const withTimeout = (promise, ms, label = 'timeout') =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(label)), ms)),
  ]);

const uploadViaCloudFunction = async (photoType, blob) => {
  const dataUrl = await blobToDataUrl(blob);
  const fn = httpsCallable(functions, 'uploadDriverPhotoSecure');
  // Cold start + rural mobile data can push a legit upload past 10s, and the
  // direct-Storage fallback is CORS-blocked inside the Capacitor WebView, so
  // this call must be given a realistic budget with one retry rather than
  // bailing early onto a path that cannot work.
  let lastErr;
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      const res = await withTimeout(fn({ photoType, base64Data: dataUrl }), 30000, 'fn_timeout');
      return res.data?.url;
    } catch (err) {
      lastErr = err;
      console.warn(`uploadDriverPhotoSecure attempt ${attempt}/2 failed:`, err?.message);
    }
  }
  throw lastErr;
};

/** Thrown when neither the Cloud Function proxy nor direct Storage could persist the image. */
const uploadFailed = () => Object.assign(new Error('upload_failed'), { code: 'upload_failed' });

/**
 * Save captain / auto photos to Cloud Storage.
 * Two tiers: Cloud Function proxy (fast, zero CORS) -> direct Storage upload.
 * There is deliberately NO data-URL fallback — base64 image blobs must never be
 * written into the drivers/{uid} Firestore doc (1 MB limit, and it bypasses
 * Storage access rules). On failure this throws `upload_failed`; the caller keeps
 * the local preview and blocks submit until a real https:// URL exists.
 */
const uploadDriverImage = async (uid, file, { storagePath, photoType = 'face', profileField, pendingField, pendingIfApproved }) => {
  if (!uid || !file) throw new Error('missing');
  const blob = await compressImage(file, 640, 0.72);
  let url = '';

  // 1. Cloud Function proxy first (fastest, bypasses browser CORS completely)
  try {
    url = await uploadViaCloudFunction(photoType, blob);
  } catch (fnErr) {
    console.warn('Cloud Function upload failed, trying direct Storage:', fnErr?.message);
    // 2. Direct Storage upload
    try {
      const sRef = storageRef(storage, storagePath);
      await withTimeout(uploadBytes(sRef, blob, { contentType: 'image/jpeg' }), 8000, 'storage_timeout');
      url = await withTimeout(getDownloadURL(sRef), 4000, 'get_url_timeout');
    } catch (storageErr) {
      console.warn('Direct Storage upload failed:', storageErr?.message);
      throw uploadFailed();
    }
  }

  // Storage is the only acceptable destination.
  if (!url || !url.startsWith('https://')) {
    throw uploadFailed();
  }

  let usePending = false;
  if (pendingIfApproved && pendingField) {
    try {
      const snap = await getDoc(doc(db, 'drivers', uid));
      usePending = snap.exists() && snap.data()?.isApproved === true;
    } catch { /* best-effort */ }
  }

  try {
    if (usePending) {
      await updateDriverProfile(uid, {
        [pendingField]: url,
        photosPendingReview: true,
      });
    } else {
      await updateDriverProfile(uid, { [profileField]: url });
    }
  } catch (dbErr) {
    console.warn('Driver photo field update non-fatal error:', dbErr?.message);
  }
  return url;
};

/** Compress image client-side and upload to Storage (falls back to data URL). */
export const uploadDriverPhoto = async (uid, file, opts = {}) =>
  uploadDriverImage(uid, file, {
    storagePath: `drivers/${uid}/photo.jpg`,
    photoType: 'face',
    profileField: 'photoUrl',
    pendingField: 'pendingPhotoUrl',
    pendingIfApproved: !!opts.pendingIfApproved,
  });

/** Auto / vehicle photo for admin verification. */
export const uploadDriverVehiclePhoto = async (uid, file, opts = {}) =>
  uploadDriverImage(uid, file, {
    storagePath: `drivers/${uid}/vehicle.jpg`,
    photoType: 'auto',
    profileField: 'vehiclePhotoUrl',
    pendingField: 'pendingVehiclePhotoUrl',
    pendingIfApproved: !!opts.pendingIfApproved,
  });

/** Custom UPI QR code photo uploaded by captain. */
export const uploadDriverUpiQr = async (uid, file) =>
  uploadDriverImage(uid, file, {
    storagePath: `drivers/${uid}/upi_qr.jpg`,
    photoType: 'upi_qr',
    profileField: 'upiQrUrl',
  });

/**
 * KYC Documents (DL, RC, Insurance) photo uploader.
 * Cloud Function proxy -> direct Storage. No data-URL fallback (KYC images must
 * never land in Firestore). Throws `upload_failed` if both tiers fail.
 */
export const uploadDriverDocPhoto = async (uid, docKey, file) => {
  const blob = await compressImage(file, 1024, 0.78);
  const sPath = `drivers/${uid}/docs/${docKey}_${Date.now()}.jpg`;

  // 1. Cloud Function proxy first (instant, tokenized, zero CORS delay)
  try {
    const url = await uploadViaCloudFunction(docKey, blob);
    if (url && url.startsWith('https://')) return url;
  } catch (fnErr) {
    console.warn('Doc Cloud Function upload failed, trying direct Storage:', fnErr?.message);
  }

  // 2. Direct Storage
  try {
    const sRef = storageRef(storage, sPath);
    await withTimeout(uploadBytes(sRef, blob, { contentType: 'image/jpeg' }), 8000, 'storage_timeout');
    const url = await withTimeout(getDownloadURL(sRef), 4000, 'get_url_timeout');
    if (url && url.startsWith('https://')) return url;
  } catch (storageErr) {
    console.warn('Doc direct Storage upload failed:', storageErr?.message);
  }

  throw uploadFailed();
};

export const updateDriverDocuments = async (uid, docData) => {
  await setDoc(doc(db, 'drivers', uid), {
    documents: stripDataUrls(docData),
    documentsSubmittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

const compressImage = (file, maxW, quality) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, maxW / img.width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('compress_failed'))), 'image/jpeg', quality);
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('image_load_failed'));
  };
  img.src = url;
});

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

export const publishDriverLocation = async (driverId, lat, lng, heading = null, { force = false } = {}) => {
  if (!driverId || lat == null || lng == null) return;
  const now = Date.now();
  const prev = publishDriverLocation._last?.get(driverId);
  // 1.2s / 4m threshold for smooth high-framerate live tracking with zero lag.
  // force bypasses it — used once, when going online, to guarantee this exact
  // write actually reaches RTDB instead of silently no-op'ing against a
  // just-published nearby point.
  if (!force && prev && now - prev.at < 1200) {
    const dlat = (lat - prev.lat) * 111000;
    const dlng = (lng - prev.lng) * 111000 * Math.cos((lat * Math.PI) / 180);
    if (Math.hypot(dlat, dlng) < 4) return;
  }
  if (!publishDriverLocation._last) publishDriverLocation._last = new Map();
  publishDriverLocation._last.set(driverId, { at: now, lat, lng });

  const locRef = ref(rtdb, `driverLocations/${driverId}`);

  // Keep the last coordinate on an unexpected disconnect. Normal dispatch
  // still ignores disconnected/stale rows; the final recovery ring can use a
  // bounded last-known in-service-area position instead of losing an Anna
  // completely. Explicit offline/logout calls clearDriverLocation below.
  if (!publishDriverLocation._discAttached?.has(driverId)) {
    if (!publishDriverLocation._discAttached) publishDriverLocation._discAttached = new Set();
    publishDriverLocation._discAttached.add(driverId);
    try {
      onDisconnect(locRef).update({
        connected: false,
        disconnectedAt: rtdbServerTimestamp(),
      }).catch(() => {});
    } catch { /* noop */ }
  }

  await set(locRef, {
    lat,
    lng,
    heading: heading != null ? Math.round(heading) : null,
    updatedAt: now,
    presenceAt: now,
    connected: true,
  });
};

/**
 * Native Android tracking owns RTDB presence once its foreground service has
 * started. Cancel only this WebView connection's onDisconnect handler without
 * deleting the shared last-known location; otherwise closing the WebView can
 * make a still-running native service look offline.
 */
export const relinquishWebDriverLocationPresence = async (driverId) => {
  if (!driverId) return;
  publishDriverLocation._discAttached?.delete(driverId);
  try {
    await onDisconnect(ref(rtdb, `driverLocations/${driverId}`)).cancel();
  } catch (err) {
    // Native presence remains safe even if this best-effort cleanup is delayed;
    // the native heartbeat will assert connected:true on its next cycle.
    console.warn('relinquish web location presence failed:', err?.message || err);
  }
};

export const clearDriverLocation = async (driverId) => {
  if (!driverId) return;
  publishDriverLocation._discAttached?.delete(driverId);
  try {
    const locRef = ref(rtdb, `driverLocations/${driverId}`);
    // Cancel before deleting. Otherwise a queued disconnect update can race
    // this explicit offline action and recreate a false last-known presence.
    await onDisconnect(locRef).cancel();
    await set(locRef, null);
  } catch (err) {
    console.warn('clearDriverLocation failed:', err);
  }
};

export const listenToDriverLocation = (driverId, callback, onError) => {
  if (!driverId) return () => {};
  const locRef = ref(rtdb, `driverLocations/${driverId}`);
  return onValue(
    locRef,
    (snap) => callback(snap.exists() ? snap.val() : null),
    (err) => {
      console.error('listenToDriverLocation:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

export const sendRideMessage = async (rideId, senderId, text, senderRole) => {
  const cleaned = String(text || '').trim().slice(0, 480);
  if (!cleaned) throw new Error('empty');
  await addDoc(collection(db, 'rides', rideId, 'messages'), {
    senderId,
    senderRole,
    text: cleaned,
    createdAt: serverTimestamp(),
  });
};

export const listenToRideMessages = (rideId, callback) => {
  const q = query(
    collection(db, 'rides', rideId, 'messages'),
    orderBy('createdAt', 'asc'),
    limit(100),
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const listenToTodayCompletedRides = (driverId, callback) => {
  if (!driverId) return () => {};
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const startMs = startOfDay.getTime();

  // Uses the (driverId, status, completedAt DESC) composite index so the query
  // returns the *most recent* completed rides — not an arbitrary 100 ordered by
  // document id (which, for a driver with >100 lifetime rides, never includes
  // today's rides and left the dashboard summary showing 0).
  const q = query(
    collection(db, 'rides'),
    where('driverId', '==', driverId),
    where('status', '==', 'completed'),
    orderBy('completedAt', 'desc'),
    limit(100),
  );

  const toMs = (raw) => {
    if (!raw) return 0;
    if (typeof raw.toMillis === 'function') return raw.toMillis();
    if (typeof raw.seconds === 'number') return raw.seconds * 1000;
    if (raw instanceof Date) return raw.getTime();
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  return onSnapshot(
    q,
    (snap) => {
      const rides = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => toMs(r.completedAt) >= startMs);
      callback(rides);
    },
    (err) => {
      console.error('listenToTodayCompletedRides failed:', err?.code, err?.message);
      callback([]);
    },
  );
};

export const submitAccountDeletionRequest = async ({ phone, reason = '', details = '', role = 'driver' }) => {
  const fn = httpsCallable(functions, 'requestAccountDeletion');
  const result = await fn({ phone, reason, details, role });
  return result.data;
};

export const recordConsent = async (uid, { purposes, noticeVersion, method }) => {
  if (!uid) return;
  await addDoc(collection(db, 'consentRecords'), {
    uid,
    purposes: Array.isArray(purposes) ? purposes.slice(0, 12) : [],
    noticeVersion: String(noticeVersion || '').slice(0, 40),
    method: String(method || 'login').slice(0, 40),
    createdAt: serverTimestamp(),
  });
};

export const savePrivacyPreferences = async (uid, _role, { analyticsOptOut, marketingOptIn }) => {
  if (!uid) return;
  try {
    localStorage.setItem('mt_analytics_opt_out', analyticsOptOut ? '1' : '0');
  } catch { /* */ }
  await setDoc(doc(db, 'drivers', uid), {
    analyticsOptOut: Boolean(analyticsOptOut),
    marketingOptIn: Boolean(marketingOptIn),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const saveNominee = async (uid, _role, nominee) => {
  if (!uid) return;
  await setDoc(doc(db, 'drivers', uid), {
    nominee: nominee
      ? {
          name: String(nominee.name || '').slice(0, 60),
          phone: String(nominee.phone || '').replace(/\D/g, '').slice(-10),
        }
      : null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const submitPrivacyRequest = async ({ type, details = '' }) => {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('not_signed_in');
  await addDoc(collection(db, 'privacyRequests'), {
    uid,
    type: String(type || 'access').slice(0, 40),
    details: String(details || '').slice(0, 1000),
    status: 'pending',
    createdAt: serverTimestamp(),
  });
};

export const attestAgeAndConsent = async (uid, noticeVersion) => {
  if (!uid) return;
  await setDoc(doc(db, 'drivers', uid), {
    ageAttested: true,
    ageAttestedAt: serverTimestamp(),
    privacyNoticeVersionAccepted: String(noticeVersion || '').slice(0, 40),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};
